// Pure planning for the per-tenant document file cache (Cache Storage
// `od-files-<tenant>`). No browser APIs — node:test covers it.
//
// Modes:  'none'      → no proactive download, cache-on-view only
//         'docs'      → all PDF/Office/XML files
//         'docs+cad'  → docs + DWG files + GLB previews of STP models
// STP originals are never pre-synced (up to 400 MB each); the 3D viewer only
// needs the server-rendered GLB preview.

export const DOC_EXT = new Set(["pdf", "docx", "doc", "xlsx", "xls", "csv", "xml", "txt"]);
export const DWG_EXT = new Set(["dwg", "dxf"]);
export const STP_EXT = new Set(["stp", "step"]);
export const MODES = ["none", "docs", "docs+cad"];

// No download dialog (2026-09-23) and no automatic download (user decision
// 2026-09-27, reverting the desktop default of 79a8e88): without a stored
// choice nothing is downloaded proactively — files are cached on view, and a
// full download happens only after choosing a mode in Einstellungen ›
// Offline-Nutzung. An explicit choice always wins.
export function resolveFileSyncMode(stored) {
  return MODES.includes(stored) ? stored : "none";
}

export function fileExt(nameOrPath) {
  if (!nameOrPath) return "";
  const last = String(nameOrPath).split("/").pop().split("?")[0];
  const i = last.lastIndexOf(".");
  return i >= 0 ? last.slice(i + 1).toLowerCase() : "";
}

export function fileNameFromPath(path) {
  try { return decodeURIComponent(String(path || "").split("/").pop()); } catch { return String(path || "").split("/").pop(); }
}

export const glbKey = (path) => `${path}/preview.glb`;

// → { key, kind: 'doc'|'glb', group: 'docs'|'cad', docId, contentHash, size } | null
export function classifyDoc(doc) {
  if (!doc || !doc.path) return null;
  const ext = fileExt(doc.path) || fileExt(doc.name);
  const base = { docId: doc.docId || doc._id || null, contentHash: doc.contentHash || null };
  if (DOC_EXT.has(ext)) return { ...base, key: doc.path, kind: "doc", group: "docs", size: doc.fileSize ?? doc.file_size ?? null };
  if (DWG_EXT.has(ext)) return { ...base, key: doc.path, kind: "doc", group: "cad", size: doc.fileSize ?? doc.file_size ?? null };
  if (STP_EXT.has(ext)) return { ...base, key: glbKey(doc.path), kind: "glb", group: "cad", size: doc.glbSize ?? null };
  return null;
}

export function wantedGroups(mode) {
  if (mode === "docs") return new Set(["docs"]);
  if (mode === "docs+cad") return new Set(["docs", "cad"]);
  return new Set();
}

// Thumbnails exist for PDF and DOCX only (server route restriction).
export function thumbnailKeys(docs) {
  const out = [];
  for (const d of docs || []) {
    if (!d?.path) continue;
    const ext = fileExt(d.path);
    if (ext === "pdf" || ext === "docx") out.push(`/api/thumbnail/${d.path.split("/").pop()}`);
  }
  return out;
}

// states: Map<key, {contentHash, source:'sync'|'view'}>   cachedKeys: Set<key>
export function planFileSync(docs, { mode = "none", states = new Map(), cachedKeys = new Set() } = {}) {
  const wanted = wantedGroups(mode);
  const known = new Map();
  for (const d of docs || []) {
    const e = classifyDoc(d);
    if (e) known.set(e.key, e);
  }
  const toFetch = [];
  let skipped = 0;
  for (const e of known.values()) {
    if (!wanted.has(e.group)) continue;
    const st = states.get(e.key);
    const fresh = cachedKeys.has(e.key) && st && (st.contentHash || null) === (e.contentHash || null);
    if (fresh) skipped++; else toFetch.push(e);
  }
  const toDelete = [];
  for (const key of cachedKeys) {
    const e = known.get(key);
    if (!e) { toDelete.push(key); continue; }                          // document removed / unknown file
    if (!wanted.has(e.group) && states.get(key)?.source !== "view") toDelete.push(key); // mode downgrade; viewed files stay
  }
  return { toFetch, toDelete, skipped };
}

export function estimateByMode(docs) {
  const acc = { docs: { count: 0, bytes: 0, unknown: 0 }, cad: { count: 0, bytes: 0, unknown: 0 } };
  for (const d of docs || []) {
    const e = classifyDoc(d);
    if (!e) continue;
    const g = acc[e.group];
    g.count++;
    if (typeof e.size === "number" && e.size > 0) g.bytes += e.size; else g.unknown++;
  }
  return acc;
}
